(() => {
  const style = document.createElement('style');
  style.id = 'bliss-cart-ux-fix';
  style.textContent = `
    cart-drawer .drawer__inner {
      height: 100dvh;
      max-height: 100dvh;
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }
    cart-drawer-items {
      min-height: 0;
      overflow: auto !important;
      flex: 1 1 auto;
    }
    cart-drawer .drawer__contents { min-height: 0; }
    cart-drawer .drawer__inner > .drawer__footer {
      flex: 0 0 auto;
      max-height: 44dvh;
      overflow-y: auto;
      overscroll-behavior: contain;
      position: relative;
      padding-bottom: 0;
    }
    cart-drawer .drawer__inner > .drawer__footer > .cart__ctas {
      position: sticky;
      bottom: 0;
      z-index: 20;
      background: rgb(var(--color-background));
      padding: 1rem 0 max(1rem, env(safe-area-inset-bottom));
      margin-top: 0;
    }
    cart-drawer #CartDrawer-Checkout {
      display: block;
      width: 100%;
      max-width: none;
      margin: 0;
      min-height: 4.8rem;
    }
    @media screen and (max-height: 650px) {
      cart-drawer-items { overflow: auto !important; }
      cart-drawer .drawer__inner { overflow: hidden !important; }
      cart-drawer .drawer__inner > .drawer__footer { max-height: 52dvh; }
    }
  `;
  document.head.appendChild(style);
})();

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.blissChatObserver = null;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  hideBlissChatWidgets() {
    const hiddenElements = new Set();

    const hideElement = (element) => {
      if (!element || element.closest?.('cart-drawer')) return;
      if (element.dataset?.blissCartHidden === 'true') return;
      element.dataset.blissCartHidden = 'true';
      element.dataset.blissCartPrevDisplay = element.style.display || '';
      element.style.setProperty('display', 'none', 'important');
      hiddenElements.add(element);
    };

    const inspectElement = (element) => {
      if (!(element instanceof HTMLElement)) return;

      if (element instanceof HTMLIFrameElement) {
        const src = (element.getAttribute('src') || '').toLowerCase();
        if (src.includes('ailead')) {
          hideElement(element.parentElement || element);
          return;
        }
      }

      const idClass = `${element.id || ''} ${element.className || ''}`.toLowerCase();
      if (idClass.includes('ailead')) {
        let host = element;
        while (host.parentElement && host.parentElement !== document.body) {
          const style = getComputedStyle(host);
          if (style.position === 'fixed') break;
          host = host.parentElement;
        }
        hideElement(host);
      }
    };

    const findChatText = (root) => {
      const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT);
      let node = walker.currentNode;
      while (node) {
        if (node instanceof HTMLElement) {
          inspectElement(node);
          const text = (node.innerText || '').trim().toLowerCase();
          if (text === 'chat with us' || text.includes('chat with us')) {
            let host = node;
            while (host.parentElement && host.parentElement !== document.body) {
              const style = getComputedStyle(host);
              if (style.position === 'fixed') break;
              host = host.parentElement;
            }
            if (getComputedStyle(host).position === 'fixed') hideElement(host);
          }
          if (node.shadowRoot) findChatText(node.shadowRoot);
        }
        node = walker.nextNode();
      }
    };

    findChatText(document.body);

    if (!this.blissChatObserver) {
      this.blissChatObserver = new MutationObserver(() => findChatText(document.body));
      this.blissChatObserver.observe(document.body, { childList: true, subtree: true });
    }
  }

  restoreBlissChatWidgets() {
    this.blissChatObserver?.disconnect();
    this.blissChatObserver = null;
    document.querySelectorAll('[data-bliss-cart-hidden="true"]').forEach((element) => {
      const previous = element.dataset.blissCartPrevDisplay || '';
      element.style.removeProperty('display');
      if (previous) element.style.display = previous;
      delete element.dataset.blissCartHidden;
      delete element.dataset.blissCartPrevDisplay;
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    this.hideBlissChatWidgets();
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.hideBlissChatWidgets();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    this.restoreBlissChatWidgets();
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
